import {
  SourceEntityMigrationType,
  type ISourceEntity,
  ISourceEntityTypes,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  ISODateType,
  Timezone,
  TypeGuard,
  toFloat,
  toISODate,
} from '@principle-theorem/shared';
import { flow, isBoolean, isNumber, isString } from 'lodash';
import * as moment from 'moment-timezone';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { SourceEntity } from '../../../source/source-entity';
import {
  KEYSET_PLACEHOLDER,
  convertKeysToCamelCaseFn,
  convertNullToUndefinedFn,
  convertValueFn,
} from '../../../source/source-helpers';
import { EXACT_NULL_SENTINEL } from '../../util/helpers';
import { ExactV2HolderSplit } from '../../lib/exact-v2-holder-split';
import {
  ExactV2AdjustmentType,
  ExactV2TransactionType,
} from '../../lib/exact-v2-transaction-type';
import { PatientSourceEntity } from './patient';

export const PATIENT_TRANSACTIONS_V2_RESOURCE_TYPE = 'patientTransactionV2';

export const PATIENT_TRANSACTIONS_V2_SOURCE_ENTITY: ISourceEntity =
  SourceEntity.init({
    metadata: {
      label: 'Patient Transactions V2 List',
      description: '',
      idPrefix: PATIENT_TRANSACTIONS_V2_RESOURCE_TYPE,
      migrationType: SourceEntityMigrationType.Automatic,
    },
  });

const EXCLUDED_TRNSTYPES = [
  ExactV2TransactionType.StatementPrinted,
  ExactV2TransactionType.RecallPrinted,
  ExactV2TransactionType.DocumentPrinted,
  ExactV2TransactionType.MedicareClaimPrinted,
  ExactV2TransactionType.InvalidJunk,
  ExactV2TransactionType.Empty,
].join(', ');

export interface IExactV2TransactionResult {
  id: string;
  /**
   * Resolved patient this row belongs to. For agreement-funded rows this is
   * the real patient, rewritten from the holder account via the CotGuid
   * lookup. Equal to source_patient_id for normal rows.
   */
  patient_id: string;
  /**
   * Original Exact DebtorId (row owner) before re-attribution. Kept as an
   * audit field so we can trace holder-ledger rewrites.
   */
  source_patient_id: string;
  transaction_type: number;
  date: string;
  entry_date: string;
  amount: number;
  gst_amount: number;
  amount_open: number;
  reference?: string;
  provider_code?: string;
  entered_by?: string;
  /** Invoice id — Charge rows only. */
  invoice_id?: string;
  /** Course-of-treatment id — Charge and Adjustment rows. */
  course_of_treatment_id?: string;
  /** Receipt id — Receipt rows only. */
  receipt_id?: string;
  /** Deposit id — Deposit rows only. */
  deposit_id?: string;
  /** Trimmed Comment field. Holds patient name on agreement-funded rows. */
  comment?: string;
  /** Adjustment subcategory e.g. 'RECEIPTCOR', 'INV_DISCNT', 'PAYMENTSCH'. */
  adjustment_label?: string;
  /** Adjustment side: 1=invoice, 2=receipt. Undefined for non-adjustments. */
  adjustment_type?: number;
  is_discount: boolean;
  is_resubmission_adjustment: boolean;
  /** Original transaction this adjustment corrects. */
  adjusted_transaction_id?: string;
  is_opening_balance_transaction: boolean;
  /** HICAPS terminal fields — populated in AU practices, typically empty for NZ. */
  hicaps_terminal_number?: string;
  hicaps_status?: string;
  receipt_type: number;
  payment_status: number;
  /** External payor reference. */
  payor_id?: string;
  /** True when an agreement holder owns the ledger the row sits on. */
  is_agreement_funded?: boolean;
  invoice_status?: string;
  /** Groups charges from the same appointment to the same invoice line set. */
  invoice_line_group_id?: string;
  is_deleted: boolean;
}

/**
 * Domain shape for a transaction row.
 *
 * `patientId` is the **resolved** patient. For agreement-funded rows
 * (ACC/MOH/GDB/SDB) it is the real patient, rewritten from the holder account
 * via the CotGuid lookup. For normal rows it matches `sourcePatientId`.
 *
 * `sourcePatientId` is the original Exact DebtorId (row owner) before
 * re-attribution. Kept as an audit field so we can trace holder-ledger
 * rewrites when validating balances against debtacc3.
 */
export interface IExactV2Transaction {
  id: string;
  patientId: string;
  sourcePatientId: string;
  transactionType: ExactV2TransactionType;
  date: string;
  entryDate: string;
  amount: number;
  gstAmount: number;
  amountOpen: number;
  reference?: string;
  providerCode?: string;
  enteredBy?: string;
  invoiceId?: string;
  courseOfTreatmentId?: string;
  receiptId?: string;
  depositId?: string;
  comment?: string;
  adjustmentLabel?: string;
  adjustmentType?: ExactV2AdjustmentType;
  isDiscount: boolean;
  isResubmissionAdjustment: boolean;
  adjustedTransactionId?: string;
  isOpeningBalanceTransaction: boolean;
  hicapsTerminalNumber?: string;
  hicapsStatus?: string;
  receiptType: number;
  paymentStatus: number;
  payorId?: string;
  isAgreementFunded?: boolean;
  invoiceStatus?: string;
  invoiceLineGroupId?: string;
  isDeleted: boolean;
}

export interface IExactV2TransactionTranslations {
  date: ISODateType;
}

export interface IExactV2TransactionFilters {
  id: string;
  patientId: string;
  date: ISODateType;
}

function isExactV2Transaction(
  data: unknown,
  logError: boolean
): data is IExactV2Transaction {
  return TypeGuard.interface<IExactV2Transaction>(
    {
      id: isString,
      patientId: isString,
      sourcePatientId: isString,
      transactionType: TypeGuard.enumValue(ExactV2TransactionType),
      date: isString,
      entryDate: isString,
      amount: isNumber,
      gstAmount: isNumber,
      amountOpen: isNumber,
      reference: TypeGuard.nilOr(isString),
      providerCode: TypeGuard.nilOr(isString),
      enteredBy: TypeGuard.nilOr(isString),
      invoiceId: TypeGuard.nilOr(isString),
      courseOfTreatmentId: TypeGuard.nilOr(isString),
      receiptId: TypeGuard.nilOr(isString),
      depositId: TypeGuard.nilOr(isString),
      comment: TypeGuard.nilOr(isString),
      adjustmentLabel: TypeGuard.nilOr(isString),
      adjustmentType: TypeGuard.nilOr(
        TypeGuard.enumValue(ExactV2AdjustmentType)
      ),
      isDiscount: isBoolean,
      isResubmissionAdjustment: isBoolean,
      adjustedTransactionId: TypeGuard.nilOr(isString),
      isOpeningBalanceTransaction: isBoolean,
      hicapsTerminalNumber: TypeGuard.nilOr(isString),
      hicapsStatus: TypeGuard.nilOr(isString),
      receiptType: isNumber,
      paymentStatus: isNumber,
      payorId: TypeGuard.nilOr(isString),
      isAgreementFunded: TypeGuard.nilOr(isBoolean),
      invoiceStatus: TypeGuard.nilOr(isString),
      invoiceLineGroupId: TypeGuard.nilOr(isString),
      isDeleted: isBoolean,
    },
    logError
  )(data);
}

/**
 * Pulls patient transactions from the flat ledger (trans4) and re-attributes
 * agreement-funded rows to the real patient.
 *
 * Excluded row kinds (see EXCLUDED_TRNSTYPES): statement printed, recall
 * printed, corrupt junk rows, and empty init rows — all carry no money.
 *
 * Three CTEs drive re-attribution:
 *   1. agreement_holders — accounts that act as ledger payors (ACC, MOH, GDB,
 *      SDB-*, etc.) detected via trans4.payorid.
 *   2. cot_to_real_debtor — course-of-treatment id → real patient lookup.
 *   3. holder_receipt_splits — batch payor receipt → one row per patient paid.
 *
 * trans4.payorid points into two namespaces:
 *   4294967295           — no payor (private patient).
 *   in localpayor        — scheme payor (ACC, GDB-*, SDB-*, BD, etc.).
 *   otherwise            — a debtor account acting as agreement holder.
 *
 * After re-attribution: patient_id is the real patient, source_patient_id keeps
 * the original holder. Only rows nothing can be attributed to are dropped —
 * orphan self-charges on a holder ledger, and holder receipts that settle
 * nothing but those.
 */

const TRANSACTION_V2_SOURCE_QUERY = `
WITH agreement_holders AS (
  SELECT DISTINCT payorid AS debtorid
  FROM trans4
  WHERE
    NOT COALESCE(_is_deleted, false)
    AND payorid != ${EXACT_NULL_SENTINEL}
    AND payorid NOT IN (SELECT _record_id FROM localpayor)
  UNION
  SELECT _record_id AS debtorid FROM localpayor
),
cot_to_real_debtor AS (
  SELECT
    cotguid AS cot_guid,
    MIN(debtorid) AS real_debtor_id
  FROM trans4
  WHERE
    NOT COALESCE(_is_deleted, false)
    AND debtorid != ${EXACT_NULL_SENTINEL}
    AND cotguid != ${EXACT_NULL_SENTINEL}
    AND debtorid NOT IN (SELECT debtorid FROM agreement_holders)
  GROUP BY cotguid
),
-- One holder receipt batch-settles charges across many patients, so it fans
-- out into one row per patient via its allocation legs.
holder_receipt_splits AS MATERIALIZED (
  SELECT
    receipt.debtorid AS holder_id,
    allocation.drtrnsid AS receipt_id,
    allocation.dr_date AS receipt_date,
    resolved.patient_id,
    -- Legs only. The receipt's own amount covers every patient it paid for.
    SUM(convert_to_decimal(allocation.amount)) AS amount,
    SUM(COALESCE(convert_to_decimal(allocation.gstamount), 0)) AS gst_amount
  FROM trnsallo allocation
  JOIN trans4 receipt
    ON receipt._record_id = allocation.drtrnsid
    AND receipt.date = allocation.dr_date
  JOIN trans4 charge
    ON charge._record_id = allocation.crtrnsid
    AND charge.date = allocation.cr_date
  LEFT JOIN cot_to_real_debtor charge_cot
    ON charge_cot.cot_guid = charge.cotguid
  LEFT JOIN cot_to_real_debtor receipt_cot
    ON receipt_cot.cot_guid = receipt.cotguid
  CROSS JOIN LATERAL (
    SELECT COALESCE(
      CASE
        WHEN charge.debtorid IN (SELECT debtorid FROM agreement_holders)
        THEN charge_cot.real_debtor_id
        ELSE NULL
      END,
      charge.debtorid
    ) AS patient_id
  ) resolved
  WHERE
    NOT COALESCE(allocation._is_deleted, false)
    AND NOT COALESCE(receipt._is_deleted, false)
    AND NOT COALESCE(charge._is_deleted, false)
    AND ${ExactV2HolderSplit.isSplitPayment('receipt', 'receipt_cot')}
    -- The main query drops this charge as an orphan, so drop it here too.
    AND NOT (
      charge.debtorid IN (SELECT debtorid FROM agreement_holders)
      AND charge_cot.real_debtor_id IS NULL
    )
  GROUP BY
    receipt.debtorid,
    allocation.drtrnsid,
    allocation.dr_date,
    resolved.patient_id
)
SELECT
  -- A split appears once per patient it settles, so it needs its own id.
  CASE
    WHEN holder_receipt_splits.patient_id IS NOT NULL
    THEN ${ExactV2HolderSplit.splitId(
      'trans4._record_id',
      'holder_receipt_splits.patient_id'
    )}
    ELSE trans4._record_id::TEXT
  END                                                            AS id,

  -- On a holder ledger? Rewrite to the real patient via CotGuid. Otherwise pass through.
  COALESCE(
    holder_receipt_splits.patient_id,
    CASE
      WHEN trans4.debtorid IN (SELECT debtorid FROM agreement_holders)
      THEN cot_to_real_debtor.real_debtor_id
      ELSE NULL
    END,
    trans4.debtorid
  )::TEXT                                                        AS patient_id,

  -- Audit field: the original Exact DebtorId, never rewritten.
  trans4.debtorid::TEXT                                          AS source_patient_id,

  convert_to_integer(trans4.trnstype)                            AS transaction_type,
  -- Some holder receipts carry no date, only the day they were entered.
  COALESCE(NULLIF(trans4.date::TEXT, ''), trans4.entry_date::TEXT) AS date,
  trans4.entry_date::TEXT                                        AS entry_date,
  COALESCE(
    holder_receipt_splits.amount,
    convert_to_decimal(trans4.amount)
  )                                                              AS amount,
  COALESCE(
    holder_receipt_splits.gst_amount,
    NULLIF(convert_to_decimal(trans4.gst_amount), 'NaN'),
    0
  )                                                              AS gst_amount,
  -- What the receipt left open belongs to no patient, so it stays on the holder.
  CASE
    WHEN holder_receipt_splits.patient_id IS NOT NULL THEN 0
    ELSE COALESCE(convert_to_decimal(trans4.amount_open), 0)
  END                                                            AS amount_open,
  NULLIF(trans4.trnsref, '')                                    AS reference,
  NULLIF(trans4.providercode, '')                               AS provider_code,
  NULLIF(trans4.enteredby, '')                                  AS entered_by,
  NULLIF(NULLIF(trans4.invoicenumguid::TEXT, '${EXACT_NULL_SENTINEL}'), '0') AS invoice_id,
  NULLIF(NULLIF(trans4.cotguid::TEXT, '${EXACT_NULL_SENTINEL}'), '0')        AS course_of_treatment_id,
  NULLIF(NULLIF(trans4.receiptnumguid::TEXT, '${EXACT_NULL_SENTINEL}'), '0') AS receipt_id,
  NULLIF(NULLIF(trans4.depositnumguid::TEXT, '${EXACT_NULL_SENTINEL}'), '0') AS deposit_id,
  NULLIF(TRIM(trans4.comment), '')                              AS comment,
  NULLIF(trans4.adjtype, '')                                    AS adjustment_label,
  convert_to_integer(NULLIF(trans4.adjustmenttype::TEXT, '0'))  AS adjustment_type,
  convert_to_boolean(trans4.isdiscount)                          AS is_discount,
  convert_to_boolean(trans4.isresubmissionadjustment)            AS is_resubmission_adjustment,
  NULLIF(NULLIF(trans4.adjustedtrnsid::TEXT, '${EXACT_NULL_SENTINEL}'), '0') AS adjusted_transaction_id,
  convert_to_boolean(trans4.isopeningbalancetransaction)         AS is_opening_balance_transaction,
  NULLIF(trans4.hicapsterminalnum, '')                          AS hicaps_terminal_number,
  NULLIF(trans4.hicapsstatus::TEXT, '0')                        AS hicaps_status,
  convert_to_integer(COALESCE(trans4.receipttype, 0))            AS receipt_type,
  convert_to_integer(COALESCE(trans4.paymentstatus, 0))          AS payment_status,
  -- A holder receipt leaves payorid as the sentinel because the holder itself
  -- is the payor. Read it off the ledger owner or ACC/MOH identity is lost.
  CASE
    WHEN holder_receipt_splits.patient_id IS NOT NULL
    THEN trans4.debtorid::TEXT
    ELSE NULLIF(trans4.payorid::TEXT, '${EXACT_NULL_SENTINEL}')
  END                                                            AS payor_id,

  -- Only a holder-owned row is the funder's debt. A row on the patient's own
  -- ledger that names the holder in payorid still counts in the patient's debtacc3.
  EXISTS (
    SELECT 1 FROM agreement_holders
    WHERE agreement_holders.debtorid = trans4.debtorid
  )                                                              AS is_agreement_funded,
  NULLIF(trans4.invoicestatus::TEXT, '0')                       AS invoice_status,
  NULLIF(NULLIF(trans4.invlinegroupguid::TEXT, '${EXACT_NULL_SENTINEL}'), '0') AS invoice_line_group_id,
  COALESCE(trans4._is_deleted, false)                            AS is_deleted
FROM
  trans4
  LEFT JOIN cot_to_real_debtor
    ON cot_to_real_debtor.cot_guid = trans4.cotguid
  -- _record_id is a reused counter, so the owner must be part of the match.
  LEFT JOIN holder_receipt_splits
    ON holder_receipt_splits.receipt_id = trans4._record_id
    AND holder_receipt_splits.receipt_date = trans4.date
    AND holder_receipt_splits.holder_id = trans4.debtorid
WHERE
  trans4.trnstype NOT IN (${EXCLUDED_TRNSTYPES})
  AND COALESCE(trans4._is_deleted, false) = false
  AND trans4.debtorid != ${EXACT_NULL_SENTINEL}
  -- Drop orphan holder rows that can't be re-attributed (usually
  --     principal self-charges, rare, <1% of value).
  AND NOT (
    trans4.debtorid IN (SELECT debtorid FROM agreement_holders)
    AND cot_to_real_debtor.real_debtor_id IS NULL
    AND holder_receipt_splits.patient_id IS NULL
  )
  -- Drop holder receipts that settle nothing we keep; the rest are here as splits.
  AND NOT (
    trans4.debtorid IN (SELECT debtorid FROM agreement_holders)
    AND trans4.trnstype = ${ExactV2TransactionType.Receipt}
    AND holder_receipt_splits.patient_id IS NULL
  )
${KEYSET_PLACEHOLDER}
`;

export type ExactV2Transaction = ISourceEntityTypes<
  IExactV2Transaction,
  IExactV2TransactionTranslations,
  IExactV2TransactionFilters
>;

export class PatientTransactionsV2SourceEntity extends BaseSourceEntity<ExactV2Transaction> {
  sourceEntity = PATIENT_TRANSACTIONS_V2_SOURCE_ENTITY;
  entityResourceType = PATIENT_TRANSACTIONS_V2_RESOURCE_TYPE;
  sourceQuery = TRANSACTION_V2_SOURCE_QUERY;
  verifySourceFn = isExactV2Transaction;
  override defaultOffsetSize = 20000;
  allowOffsetJobs = true;
  override keysetPagination = { cursorColumn: 'trans4._record_id' };

  override requiredEntities = {
    patients: new PatientSourceEntity(),
  };

  override transformDataFn = flow([
    convertNullToUndefinedFn<IExactV2TransactionResult>(),
    convertValueFn(toFloat, 'amount', 'gst_amount', 'amount_open'),
    convertKeysToCamelCaseFn<IExactV2TransactionResult>(),
  ]);

  getSourceRecordId(data: IExactV2Transaction): string {
    return data.id;
  }

  getSourceLabel(record: IExactV2Transaction): string {
    return `${record.patientId} ${record.id}`;
  }

  translate(
    data: IExactV2Transaction,
    _timezone: Timezone
  ): IExactV2TransactionTranslations {
    return {
      date: toISODate(moment(data.date)),
    };
  }

  getFilterData(
    data: IExactV2Transaction,
    _timezone: Timezone
  ): IExactV2TransactionFilters {
    return {
      id: data.id,
      patientId: data.patientId,
      date: toISODate(moment(data.date)),
    };
  }
}
